/**
 * voucher-types.js — the single source of truth for how each voucher type
 * behaves.
 *
 * WHY THIS EXISTS
 * v1 implemented sales invoice, purchase invoice, sales return, purchase
 * return and expense as five near-verbatim copies of a ~1,200-line component
 * (the diff between the sales and purchase forms was essentially a
 * `salesData` -> `purchaseData` rename). The copies then drifted: the sales
 * return posted a DEBIT where a credit note requires a CREDIT, expenses wrote
 * to the Purchases collection, and the payment input in the return forms read
 * from a state key nothing ever wrote to.
 *
 * In v2 there is one voucher engine, and the five types are five entries in
 * this table. A behavioural difference has to be expressed here, where it is
 * visible next to its siblings.
 *
 *   stockDirection   +1 adds stock, -1 removes it, 0 does not touch stock
 *   ledgerDirection  +1 increases what the party owes us (a debit to them),
 *                    -1 decreases it (a credit). A sales invoice makes the
 *                    customer owe us more; a credit note reduces that.
 *   priceField       which price on the item master seeds a new line
 *
 * NON-POSTING DOCUMENTS
 *
 *   posts            whether this document hits the PARTY LEDGER at all.
 *
 * This is the axis the original five types did not need and the reason
 * quotations could not be added before. A customer asking "how much for this
 * lot?" wants a document; what they must NOT get is a tax invoice, because
 * raising one burns an invoice number, posts a receivable the customer does
 * not owe, and puts output tax on a supply that has not happened. That is
 * accounting damage in exchange for a price list.
 *
 * So a quotation, a proforma invoice and a sales order carry `posts: false`
 * and `stockDirection: 0` — they are records of an intention, and the ledger
 * only learns about them when one becomes an invoice.
 *
 * A DELIVERY CHALLAN is the interesting case and the reason this is two flags
 * rather than one: goods physically leave the premises, so stock MUST move,
 * but nothing is owed yet, so the ledger must NOT. `stockDirection: -1` with
 * `posts: false`.
 *
 *   convertsTo       which types a "Convert to…" action offers
 *   fulfils          which source types this document draws down, so the
 *                    source can show what is still outstanding
 *   isOrder          participates in fulfilment tracking as a source
 *   allowsPayment    whether money may be recorded against it directly
 *   secondaryDate*   the second date this type needs — a quotation expires,
 *                    an order has an expected date, an invoice has neither
 */

export const VOUCHER_TYPES = {
  /* Posting documents — these move stock, the party ledger, or both. */
  SALES: 'sales',
  SALES_RETURN: 'sales_return',
  PURCHASE: 'purchase',
  PURCHASE_RETURN: 'purchase_return',
  EXPENSE: 'expense',

  /* Non-posting documents. See the note on `posts` below. */
  QUOTATION: 'quotation',
  PROFORMA: 'proforma',
  SALES_ORDER: 'sales_order',
  DELIVERY_CHALLAN: 'delivery_challan',
  PURCHASE_ORDER: 'purchase_order',
};

export const VOUCHER_CONFIG = {
  [VOUCHER_TYPES.SALES]: {
    type: VOUCHER_TYPES.SALES,
    label: 'Sales Invoice',
    shortLabel: 'Invoice',
    plural: 'Sales Invoices',
    documentTitle: 'TAX INVOICE',
    route: 'sales',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'INV',
    stockDirection: -1,
    ledgerDirection: +1,
    priceField: 'salePrice',
    /** The counterparty is the recipient, so our state is the supplier state. */
    weAreSupplier: true,
    paymentLabel: 'Amount Received',
    paymentVerb: 'Receive Payment',
    allowsBatches: true,
    /** A sales invoice may be created from scratch. */
    requiresSource: false,
    /**
     * Everything upstream of an invoice draws down when it becomes one.
     *
     * Getting this list wrong does not throw — it silently leaves the order
     * open after it has been fully invoiced, so the goods look like they are
     * still owed to the customer. `voucher-types.test.js` asserts that every
     * type offering "Convert to X" appears in X's `fulfils`, which is what
     * caught this list being empty in the first place.
     */
    fulfils: [
      VOUCHER_TYPES.QUOTATION,
      VOUCHER_TYPES.PROFORMA,
      VOUCHER_TYPES.SALES_ORDER,
      VOUCHER_TYPES.DELIVERY_CHALLAN,
    ],
    tone: 'primary',
  },

  [VOUCHER_TYPES.SALES_RETURN]: {
    type: VOUCHER_TYPES.SALES_RETURN,
    label: 'Credit Note',
    shortLabel: 'Credit Note',
    plural: 'Sales Returns',
    documentTitle: 'CREDIT NOTE',
    route: 'sales-return',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'CN',
    // Goods come back in.
    stockDirection: +1,
    // A credit note REDUCES what the customer owes. v1 posted a debit here,
    // which increased the balance owed — the opposite of correct.
    ledgerDirection: -1,
    priceField: 'salePrice',
    weAreSupplier: true,
    paymentLabel: 'Amount Refunded',
    paymentVerb: 'Record Refund',
    allowsBatches: true,
    /** Must be raised against an existing sales invoice. */
    requiresSource: true,
    sourceType: VOUCHER_TYPES.SALES,
    tone: 'warning',
  },

  [VOUCHER_TYPES.PURCHASE]: {
    type: VOUCHER_TYPES.PURCHASE,
    label: 'Purchase Bill',
    shortLabel: 'Bill',
    plural: 'Purchases',
    documentTitle: 'PURCHASE BILL',
    route: 'purchases',
    partyLabel: 'Supplier',
    partyRole: 'supplier',
    numberPrefix: 'BILL',
    stockDirection: +1,
    // We owe the supplier, so the balance moves the other way.
    ledgerDirection: -1,
    priceField: 'purchasePrice',
    weAreSupplier: false,
    paymentLabel: 'Amount Paid',
    paymentVerb: 'Make Payment',
    allowsBatches: true,
    requiresSource: false,
    /**
     * Supplier bill numbers are external and routinely repeat across vendors.
     * v1 used the bill number as the document id and rejected duplicates, so
     * two suppliers both issuing "001" could not both be recorded.
     */
    numberIsExternal: true,
    fulfils: [VOUCHER_TYPES.PURCHASE_ORDER],
    tone: 'info',
  },

  [VOUCHER_TYPES.PURCHASE_RETURN]: {
    type: VOUCHER_TYPES.PURCHASE_RETURN,
    label: 'Debit Note',
    shortLabel: 'Debit Note',
    plural: 'Purchase Returns',
    documentTitle: 'DEBIT NOTE',
    route: 'purchase-return',
    partyLabel: 'Supplier',
    partyRole: 'supplier',
    numberPrefix: 'DN',
    stockDirection: -1,
    ledgerDirection: +1,
    priceField: 'purchasePrice',
    weAreSupplier: false,
    paymentLabel: 'Amount Received',
    paymentVerb: 'Record Refund',
    allowsBatches: true,
    requiresSource: true,
    sourceType: VOUCHER_TYPES.PURCHASE,
    tone: 'warning',
  },

  [VOUCHER_TYPES.EXPENSE]: {
    type: VOUCHER_TYPES.EXPENSE,
    label: 'Expense',
    shortLabel: 'Expense',
    plural: 'Expenses',
    documentTitle: 'EXPENSE VOUCHER',
    route: 'expenses',
    partyLabel: 'Paid To',
    partyRole: 'supplier',
    numberPrefix: 'EXP',
    // Expenses are consumed, not stocked.
    stockDirection: 0,
    ledgerDirection: -1,
    priceField: 'purchasePrice',
    weAreSupplier: false,
    paymentLabel: 'Amount Paid',
    paymentVerb: 'Record Payment',
    allowsBatches: false,
    requiresSource: false,
    /** An expense may be recorded without a party at all. */
    partyOptional: true,
    tone: 'muted',
  },
  /* ---------------------------------------------------------------- *
   * Non-posting sales documents
   * ---------------------------------------------------------------- */

  [VOUCHER_TYPES.QUOTATION]: {
    type: VOUCHER_TYPES.QUOTATION,
    label: 'Quotation',
    shortLabel: 'Quotation',
    plural: 'Quotations',
    documentTitle: 'QUOTATION',
    route: 'quotations',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'QT',
    /* No settings document exists for this type, so it printed on the
       Classic tax-invoice layout by default and its line table ran off the
       page. See the `order` entry in features/invoice/templates.js. */
    defaultTemplate: 'order',
    stockDirection: 0,
    /* Kept for the payment-direction helpers even though nothing posts. */
    ledgerDirection: +1,
    posts: false,
    priceField: 'salePrice',
    weAreSupplier: true,
    paymentLabel: 'Advance',
    paymentVerb: 'Record Advance',
    allowsPayment: false,
    allowsBatches: false,
    requiresSource: false,
    partyOptional: true,
    /**
     * A quotation may be issued WITH or WITHOUT GST.
     *
     * Quoting is not a supply: nothing moves, nothing is owed, and no tax
     * liability arises until an invoice is raised. Customers routinely ask for
     * both numbers — one to compare against a registered supplier, one against
     * an unregistered one — and a system that can only quote one of them sends
     * the user to a spreadsheet for the other, which is where quotations stop
     * matching the invoices that follow them.
     *
     * The flag is on this type ALONE. A tax invoice does not get to choose
     * whether it charges tax, and the conversion drops the flag rather than
     * carrying it — see `sourceDraft` in useVoucherDraft.
     */
    allowsGstChoice: true,
    isOrder: true,
    convertsTo: [VOUCHER_TYPES.SALES_ORDER, VOUCHER_TYPES.PROFORMA, VOUCHER_TYPES.SALES],
    fulfils: [],
    secondaryDateField: 'validUntil',
    secondaryDateLabel: 'Valid Until',
    /* A quotation nobody accepted is not a debt; it simply goes stale. */
    expiresAfterDays: 15,
    tone: 'muted',
  },

  [VOUCHER_TYPES.PROFORMA]: {
    type: VOUCHER_TYPES.PROFORMA,
    label: 'Proforma Invoice',
    shortLabel: 'Proforma',
    plural: 'Proforma Invoices',
    documentTitle: 'PROFORMA INVOICE',
    route: 'proforma',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'PI',
    /* No settings document exists for this type, so it printed on the
       Classic tax-invoice layout by default and its line table ran off the
       page. See the `order` entry in features/invoice/templates.js. */
    defaultTemplate: 'order',
    stockDirection: 0,
    ledgerDirection: +1,
    posts: false,
    priceField: 'salePrice',
    weAreSupplier: true,
    paymentLabel: 'Advance',
    paymentVerb: 'Record Advance',
    allowsPayment: false,
    allowsBatches: false,
    requiresSource: false,
    partyOptional: false,
    isOrder: true,
    convertsTo: [VOUCHER_TYPES.SALES],
    fulfils: [VOUCHER_TYPES.QUOTATION],
    /**
     * A proforma raised from a quotation RESTATES it and does not consume it.
     * Nothing has been supplied and nothing invoiced, so the quotation stays
     * outstanding and stays convertible to the tax invoice that closes it.
     * See `restatesSource` in this file.
     */
    restates: [VOUCHER_TYPES.QUOTATION],
    secondaryDateField: 'validUntil',
    secondaryDateLabel: 'Valid Until',
    expiresAfterDays: 15,
    tone: 'info',
  },

  [VOUCHER_TYPES.SALES_ORDER]: {
    type: VOUCHER_TYPES.SALES_ORDER,
    label: 'Sales Order',
    shortLabel: 'Order',
    plural: 'Sales Orders',
    documentTitle: 'SALES ORDER',
    route: 'sales-orders',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'SO',
    /* No settings document exists for this type, so it printed on the
       Classic tax-invoice layout by default and its line table ran off the
       page. See the `order` entry in features/invoice/templates.js. */
    defaultTemplate: 'order',
    /**
     * Zero, deliberately. An accepted order COMMITS stock but does not consume
     * it — the goods are still on the shelf and still available to be counted.
     * Committed quantity is tracked separately (see `lib/voucher-links.js`) and
     * shown as "reserved" against the item, rather than being faked by moving
     * the stock count, which would make a physical count disagree with the
     * system for reasons nobody could see.
     */
    stockDirection: 0,
    ledgerDirection: +1,
    posts: false,
    priceField: 'salePrice',
    weAreSupplier: true,
    paymentLabel: 'Advance Received',
    paymentVerb: 'Record Advance',
    allowsPayment: false,
    allowsBatches: false,
    requiresSource: false,
    isOrder: true,
    commitsStock: true,
    convertsTo: [VOUCHER_TYPES.DELIVERY_CHALLAN, VOUCHER_TYPES.SALES],
    fulfils: [VOUCHER_TYPES.QUOTATION],
    secondaryDateField: 'expectedDate',
    secondaryDateLabel: 'Expected Delivery',
    tone: 'primary',
  },

  [VOUCHER_TYPES.DELIVERY_CHALLAN]: {
    type: VOUCHER_TYPES.DELIVERY_CHALLAN,
    label: 'Delivery Challan',
    shortLabel: 'Challan',
    plural: 'Delivery Challans',
    documentTitle: 'DELIVERY CHALLAN',
    route: 'delivery-challans',
    partyLabel: 'Customer',
    partyRole: 'customer',
    numberPrefix: 'DC',
    /* No settings document exists for this type, so it printed on the
       Classic tax-invoice layout by default and its line table ran off the
       page. See the `order` entry in features/invoice/templates.js. */
    defaultTemplate: 'order',
    /**
     * The one document where stock and ledger part company. Rule 55 of the
     * CGST Rules covers goods moving without a tax invoice — a consignment
     * sent for approval, a job-work transfer, a delivery that will be invoiced
     * at month end. The goods have physically gone, so stock must move; nothing
     * is owed until the invoice is raised, so the ledger must not.
     */
    stockDirection: -1,
    ledgerDirection: +1,
    posts: false,
    priceField: 'salePrice',
    weAreSupplier: true,
    paymentLabel: 'Amount Received',
    paymentVerb: 'Receive Payment',
    allowsPayment: false,
    allowsBatches: true,
    requiresSource: false,
    isOrder: false,
    convertsTo: [VOUCHER_TYPES.SALES],
    fulfils: [VOUCHER_TYPES.SALES_ORDER],
    secondaryDateField: 'expectedDate',
    secondaryDateLabel: 'Dispatch Date',
    /** Rule 55(1)(c): the reason the goods are moving must be stated. */
    requiresTransportReason: true,
    tone: 'warning',
  },

  /* ---------------------------------------------------------------- *
   * Non-posting purchase documents
   * ---------------------------------------------------------------- */

  [VOUCHER_TYPES.PURCHASE_ORDER]: {
    type: VOUCHER_TYPES.PURCHASE_ORDER,
    label: 'Purchase Order',
    shortLabel: 'PO',
    plural: 'Purchase Orders',
    documentTitle: 'PURCHASE ORDER',
    route: 'purchase-orders',
    partyLabel: 'Supplier',
    partyRole: 'supplier',
    numberPrefix: 'PO',
    stockDirection: 0,
    ledgerDirection: -1,
    posts: false,
    priceField: 'purchasePrice',
    weAreSupplier: false,
    paymentLabel: 'Advance Paid',
    paymentVerb: 'Record Advance',
    allowsPayment: false,
    allowsBatches: false,
    requiresSource: false,
    isOrder: true,
    convertsTo: [VOUCHER_TYPES.PURCHASE],
    fulfils: [],
    secondaryDateField: 'expectedDate',
    secondaryDateLabel: 'Expected By',
    tone: 'info',
  },
};

/**
 * Defaults for every field the five original types never had to declare.
 *
 * Merged in by `voucherConfig`, so an entry above only states what makes it
 * different. Without this, adding an axis would mean editing all ten entries
 * and the compiler would not tell you if you missed one.
 */
export const CONFIG_DEFAULTS = {
  /** Posting is the default: the five original types all do. */
  posts: true,
  allowsPayment: true,
  allowsBatches: false,
  requiresSource: false,
  partyOptional: false,
  numberIsExternal: false,
  isOrder: false,
  commitsStock: false,
  requiresTransportReason: false,
  /** Only a quotation may choose whether it carries tax. See its entry. */
  allowsGstChoice: false,
  convertsTo: [],
  fulfils: [],
  /** Sources this type links to without consuming. See `restatesSource`. */
  restates: [],
  /**
   * The printed template a type falls back to when nothing has been configured
   * for it in Invoice settings. Null means the registry default (Classic).
   * See `templateForType` in features/vouchers/voucher-settings.js.
   */
  defaultTemplate: null,
  secondaryDateField: null,
  secondaryDateLabel: null,
  expiresAfterDays: null,
  tone: 'neutral',
};

export const ALL_VOUCHER_TYPES = Object.values(VOUCHER_TYPES);

/** Every config, with the defaults merged in. Built once, not per call. */
const RESOLVED_CONFIG = Object.fromEntries(
  Object.entries(VOUCHER_CONFIG).map(([type, config]) => [type, { ...CONFIG_DEFAULTS, ...config }])
);

export function voucherConfig(type) {
  const config = RESOLVED_CONFIG[type];
  if (!config) throw new Error(`Unknown voucher type: ${type}`);
  return config;
}

/** Same, but returns null instead of throwing. For display code. */
export function tryVoucherConfig(type) {
  return RESOLVED_CONFIG[type] || null;
}

export const ALL_CONFIGS = Object.values(RESOLVED_CONFIG);

/* ------------------------------------------------------------------ *
 * Predicates
 *
 * Named rather than inlined so the INTENT is greppable. `config.posts` at a
 * call site reads as a fact about a flag; `postsToLedger(type)` reads as a
 * question about accounting, and the next person changing the ledger code can
 * find every place that asked it.
 * ------------------------------------------------------------------ */

export const postsToLedger = (type) => !!tryVoucherConfig(type)?.posts;
export const movesStock = (type) => (tryVoucherConfig(type)?.stockDirection || 0) !== 0;
export const isOrderType = (type) => !!tryVoucherConfig(type)?.isOrder;
export const commitsStock = (type) => !!tryVoucherConfig(type)?.commitsStock;

/**
 * Whether this type may be raised against the counter instead of a party.
 *
 * Only documents WE issue, and only ones not drawn from an earlier document —
 * a credit note against an invoice inherits that invoice's customer, so
 * "Cash Sale" is not one of its options.
 */
export function allowsCashSale(config) {
  const resolved = typeof config === 'string' ? tryVoucherConfig(config) : config;
  return !!(resolved?.weAreSupplier && !resolved?.requiresSource);
}

/**
 * Whether the party field must be answered before the document can be saved.
 *
 * "Answered" includes ticking Cash Sale — that IS an answer, and an explicit
 * one. What is not allowed is leaving the field blank and having the document
 * silently print as "Cash / not recorded", which is what a quotation or a
 * sales order used to do on the strength of `partyOptional`. A user who means
 * a counter sale says so; a user who forgot the customer is told.
 *
 * `partyOptional` still stands for the one type with no counter to sell over:
 * an expense, which may genuinely have no party at all.
 */
export function partyIsRequired(config) {
  const resolved = typeof config === 'string' ? tryVoucherConfig(config) : config;
  if (!resolved) return false;
  if (allowsCashSale(resolved)) return true;
  return !resolved.partyOptional;
}

/**
 * Whether a party's outstanding balance should be shown beside their name
 * while a document of this type is being written.
 *
 * Every document that moves what the party owes, plus the two that precede one
 * — you quote and you take an order on the strength of what is already
 * outstanding, so that is exactly when the figure is worth seeing.
 */
export function showsPartyBalance(config) {
  const resolved = typeof config === 'string' ? tryVoucherConfig(config) : config;
  if (!resolved) return false;
  return [
    VOUCHER_TYPES.QUOTATION,
    VOUCHER_TYPES.SALES_ORDER,
    VOUCHER_TYPES.SALES,
    VOUCHER_TYPES.PROFORMA,
    VOUCHER_TYPES.SALES_RETURN,
    VOUCHER_TYPES.PURCHASE_RETURN,
    VOUCHER_TYPES.PURCHASE,
  ].includes(resolved.type);
}

/** The types a "Convert to…" menu should offer for a document of `type`. */
export function conversionTargets(type) {
  const config = tryVoucherConfig(type);
  if (!config) return [];
  return config.convertsTo.map((target) => voucherConfig(target));
}

/** True when creating `targetType` from `sourceType` links the two documents. */
export function fulfilsSource(targetType, sourceType) {
  const target = tryVoucherConfig(targetType);
  if (!target || !sourceType) return false;
  return target.fulfils.includes(sourceType);
}

/**
 * True when creating `targetType` from `sourceType` merely RESTATES it —
 * the same commitment written on different paper — rather than delivering
 * against it.
 *
 * THE DEFECT THIS EXISTS TO CLOSE
 * A proforma invoice raised from a quotation used to draw that quotation fully
 * down, because `fulfilsSource` was the only question asked and a proforma
 * lists the quotation among the documents it fulfils (it has to, or the
 * "Convert to Proforma" action would have no link to record). The quotation
 * was then marked closed, and "Convert to Tax Invoice" on it was disabled
 * with no explanation — so the ordinary quote → proforma → invoice sequence
 * dead-ended at the last step, which is the conversion users actually need.
 *
 * A proforma is not a delivery. Nothing has been supplied, nothing has been
 * invoiced, and the quotation is still outstanding until a tax invoice is
 * raised. So the two documents are linked and the quotation is left open.
 */
export function restatesSource(targetType, sourceType) {
  const target = tryVoucherConfig(targetType);
  if (!target || !sourceType) return false;
  return (target.restates || []).includes(sourceType);
}

/**
 * True when creating `targetType` from `sourceType` CONSUMES the source's
 * outstanding quantities. This — not `fulfilsSource` — is the question the
 * editor and the repository must ask before drawing a document down.
 */
export function drawsDownSource(targetType, sourceType) {
  return fulfilsSource(targetType, sourceType) && !restatesSource(targetType, sourceType);
}

/**
 * Voucher types grouped for navigation, in the order a shop works:
 * quote, order, deliver, invoice, credit.
 */
export const VOUCHER_GROUPS = [
  {
    id: 'sales',
    label: 'Sales',
    types: [
      VOUCHER_TYPES.QUOTATION,
      VOUCHER_TYPES.PROFORMA,
      VOUCHER_TYPES.SALES_ORDER,
      VOUCHER_TYPES.DELIVERY_CHALLAN,
      VOUCHER_TYPES.SALES,
      VOUCHER_TYPES.SALES_RETURN,
    ],
  },
  {
    id: 'purchases',
    label: 'Purchases',
    types: [
      VOUCHER_TYPES.PURCHASE_ORDER,
      VOUCHER_TYPES.PURCHASE,
      VOUCHER_TYPES.PURCHASE_RETURN,
      VOUCHER_TYPES.EXPENSE,
    ],
  },
];

/** Map a URL segment back to a voucher type. */
export function voucherTypeFromRoute(route) {
  const entry = Object.values(VOUCHER_CONFIG).find((c) => c.route === route);
  return entry ? entry.type : null;
}


